if (!window.toggleUpsellContent) {
  window.toggleUpsellContent = function (header) {
    const upsell = header.closest('.cart-upsell');
    if (!upsell) return;

    const content = upsell.querySelector('.cart-upsell__content');
    const toggle = upsell.querySelector('.cart-upsell__toggle');

    if (!content) return;

    const isOpen = content.style.display === 'block';

    content.style.display = isOpen ? 'none' : 'block';
    toggle?.classList.toggle('rotated', !isOpen);
    upsell.classList.toggle('expanded', !isOpen);
  };
}

class CartCheckboxUpsell {
  constructor(cartItemsInstance) {
    this.cartItemsInstance = cartItemsInstance;
    this.isUpdating = false;

    this.handleCheckboxChange =
      this.handleCheckboxChange.bind(this);

    document.body.addEventListener(
      'change',
      this.handleCheckboxChange
    );

    document.addEventListener(
      'cartUpdated',
      () => this.syncCheckboxState()
    );

    this.syncCheckboxState();
  }

  getCheckbox() {
    return document.querySelector('#cart-upsell-checkbox');
  }

  getVariantId() {
    return this.getCheckbox()?.dataset.variantId || null;
  }

  showLoadingSpinner() {
    document
      .querySelector('.cart-upsell__loading')
      ?.classList.remove('hidden');
  }

  hideLoadingSpinner() {
    document
      .querySelector('.cart-upsell__loading')
      ?.classList.add('hidden');
  }

  async getCart() {
    const response = await fetch('/cart.js', {
      cache: 'no-store'
    });

    if (!response.ok) {
      throw new Error(
        `Failed to fetch cart: ${response.status}`
      );
    }

    return response.json();
  }

  isFastProcessingItem(item) {
    const value = item.properties?._fast_processing;

    return (
      value === true ||
      String(value).toLowerCase() === 'true'
    );
  }

  isHiddenUpsell(item) {
    return item.properties?._is_hidden_upsell === 'true';
  }

  cartContainsOnlyGiftCards(items = []) {
    const regularItems = items.filter(
      (item) =>
        !this.isFastProcessingItem(item) &&
        !this.isHiddenUpsell(item)
    );

    if (!regularItems.length) return false;

    return regularItems.every(
      (item) =>
        item.product_type === 'Gift Card' ||
        item.product_type === 'Gift Cards' ||
        item.gift_card === true
    );
  }

  async handleCheckboxChange(event) {
    if (
      event.target.id !== 'cart-upsell-checkbox' ||
      this.isUpdating
    ) {
      return;
    }

    this.isUpdating = true;
    this.showLoadingSpinner();

    try {
      if (event.target.checked) {
        await this.addUpsell();
      } else {
        await this.removeUpsell();
      }

      await this.refreshCart();
    } catch (error) {
      console.error(
        'Failed to update VIP Priority Processing:',
        error
      );

      await this.syncCheckboxState();
    } finally {
      this.hideLoadingSpinner();
      this.isUpdating = false;
    }
  }

  async addUpsell() {
    const variantId = this.getVariantId();

    if (!variantId) {
      throw new Error('VIP upsell variant ID not found.');
    }

    const cart = await this.getCart();

    const alreadyExists = cart.items.some(
      (item) =>
        String(item.variant_id) === String(variantId) &&
        this.isFastProcessingItem(item)
    );

    if (alreadyExists) return cart;

    const response = await fetch('/cart/add.js', {
      method: 'POST',
      headers: {
        'Content-Type': 'application/json',
        Accept: 'application/json'
      },
      body: JSON.stringify({
        items: [
          {
            id: Number(variantId),
            quantity: 1,
            properties: {
              _fast_processing: 'true'
            }
          }
        ]
      })
    });

    if (!response.ok) {
      const error = await response.json().catch(() => ({}));

      throw new Error(
        error.description ||
        `Failed to add VIP upsell: ${response.status}`
      );
    }

    return response.json();
  }

  async removeUpsell() {
    const cart = await this.getCart();

    const updates = {};

    cart.items.forEach((item) => {
      if (this.isFastProcessingItem(item)) {
        updates[item.key] = 0;
      }
    });

    if (!Object.keys(updates).length) {
      return cart;
    }

    const response = await fetch('/cart/update.js', {
      method: 'POST',
      headers: {
        'Content-Type': 'application/json',
        Accept: 'application/json'
      },
      body: JSON.stringify({ updates })
    });

    if (!response.ok) {
      const error = await response.json().catch(() => ({}));

      throw new Error(
        error.description ||
        `Failed to remove VIP upsell: ${response.status}`
      );
    }

    return response.json();
  }

  async syncCheckboxState() {
    if (this.isUpdating) return;

    try {
      const cart = await this.getCart();
      const checkbox = this.getCheckbox();
      const upsell = document.querySelector('.cart-upsell');

      if (!checkbox) return;

      const hasVip = cart.items.some(
        (item) => this.isFastProcessingItem(item)
      );

      checkbox.checked = hasVip;

      if (upsell) {
        upsell.style.display =
          this.cartContainsOnlyGiftCards(cart.items)
            ? 'none'
            : '';
      }

      await this.removeOrphanedVip(cart);
    } catch (error) {
      console.warn(
        'Failed to sync VIP checkbox:',
        error
      );
    }
  }

  async removeOrphanedVip(cart) {
    const regularItems = cart.items.filter(
      (item) =>
        !this.isFastProcessingItem(item) &&
        !this.isHiddenUpsell(item)
    );

    const vipItems = cart.items.filter(
      (item) => this.isFastProcessingItem(item)
    );

    if (
      regularItems.length ||
      !vipItems.length ||
      this.isUpdating
    ) {
      return;
    }

    this.isUpdating = true;

    try {
      const updates = {};

      vipItems.forEach((item) => {
        updates[item.key] = 0;
      });

      const response = await fetch('/cart/update.js', {
        method: 'POST',
        headers: {
          'Content-Type': 'application/json',
          Accept: 'application/json'
        },
        body: JSON.stringify({ updates })
      });

      if (response.ok) {
        await this.refreshCart();
      }
    } finally {
      this.isUpdating = false;
    }
  }

  async refreshCart() {
    if (
      this.cartItemsInstance &&
      typeof this.cartItemsInstance.onCartUpdate === 'function'
    ) {
      await this.cartItemsInstance.onCartUpdate();
    }

    document.dispatchEvent(
      new CustomEvent('cartUpdated', {
        detail: {
          message: 'VIP Priority Processing updated'
        }
      })
    );

    await window.helmUpdateCartCount?.();
  }

  updateCheckboxState() {
    return this.syncCheckboxState();
  }
}

window.CartCheckboxUpsell = CartCheckboxUpsell;

function initializeCartCheckboxUpsell() {
  if (window.cartCheckboxUpsellInstance) return;

  const cartDrawerItems =
    document.querySelector('cart-drawer-items');

  const cartItems =
    document.querySelector('cart-items');

  const activeCartInstance =
    cartDrawerItems || cartItems;

  if (!activeCartInstance) return;

  window.cartCheckboxUpsellInstance =
    new CartCheckboxUpsell(activeCartInstance);
}

if (document.readyState === 'loading') {
  document.addEventListener(
    'DOMContentLoaded',
    initializeCartCheckboxUpsell,
    { once: true }
  );
} else {
  initializeCartCheckboxUpsell();
}